import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Section, Eyebrow, Timeline } from "@/components/domain/Bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ShieldCheck, Landmark, MessageCircle, Mail, Phone, Globe, Instagram, AlertTriangle,
  FileText, Users, MapPin, Building2, CheckCircle2,
} from "lucide-react";

const CHANNEL_ICON = { WhatsApp: MessageCircle, Correo: Mail, "Teléfono": Phone, "Sitio oficial": Globe, Instagram: Instagram };

export default function Transparency() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tenant/atlas").then((r) => setData(r.data)); }, []);
  const channels = data?.channels || [];
  const banks = data?.bank_accounts || [];

  const timeline = [
    { label: "Compra registrada", done: true }, { label: "Folio generado", done: true },
    { label: "Comprobante recibido", done: true }, { label: "Pago validado", done: true },
    { label: "Boletos confirmados", done: true }, { label: "Sorteo realizado", done: true },
    { label: "Ganador publicado", done: true }, { label: "Premio entregado", done: true },
  ];

  return (
    <div>
      <div className="trust-gradient text-white">
        <Section className="py-16">
          <Eyebrow><ShieldCheck className="h-3.5 w-3.5" />Centro de Transparencia</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl">Seguridad y transparencia verificable</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-300">Todo lo que necesitas para confiar: identidad, legalidad, evidencias, canales y cuentas oficiales, y prevención de fraude.</p>
        </Section>
      </div>

      <Section className="py-12">
        <Tabs defaultValue="banks">
          <TabsList className="flex-wrap" data-testid="transparency-tabs">
            <TabsTrigger value="banks">Cuentas oficiales</TabsTrigger>
            <TabsTrigger value="channels">Canales oficiales</TabsTrigger>
            <TabsTrigger value="fraud">Prevención de fraude</TabsTrigger>
            <TabsTrigger value="legal">Legalidad</TabsTrigger>
            <TabsTrigger value="process">Proceso público</TabsTrigger>
          </TabsList>

          <TabsContent value="banks" className="mt-8">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
              <AlertTriangle className="mr-2 inline h-4 w-4" />Ningún vendedor está autorizado para solicitar depósitos en cuentas personales. Verifica siempre aquí.
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {banks.map((b) => (
                <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`bank-${b.bank}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Landmark className="h-5 w-5 text-brand" /><span className="text-lg font-bold text-slate-900">{b.bank}</span></div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" />Activa</span>
                  </div>
                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><dt className="text-slate-500">Titular</dt><dd className="font-medium text-slate-900">{b.holder}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">CLABE</dt><dd className="font-mono font-semibold text-slate-900">{b.clabe}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Cuenta</dt><dd className="font-mono font-semibold text-slate-900">{b.account}</dd></div>
                  </dl>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="channels" className="mt-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {channels.map((c) => {
                const Icon = CHANNEL_ICON[c.type] || Globe;
                return (
                  <div key={c.id} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`channel-${c.type}`}>
                    <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-brand"><Icon className="h-5 w-5" /></div>
                    <div><div className="text-xs text-slate-400">{c.type}</div><div className="font-semibold text-slate-900">{c.value}</div></div>
                    {c.verified && <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-500" />}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="fraud" className="mt-8">
            <div className="grid gap-5 lg:grid-cols-2">
              {[["Cómo identificar perfiles falsos", "Solo confía en cuentas verificadas listadas aquí. Desconfía de perfiles nuevos o con pocos seguidores."],
                ["Cómo verificar el dominio", "Nuestro dominio oficial es circulodorado.mx. Revisa siempre la barra de direcciones."],
                ["Cómo comprobar una cuenta bancaria", "Compara la CLABE con las publicadas en esta página. Nunca deposites a cuentas personales."],
                ["Qué nunca solicitamos", "Nunca pedimos contraseñas, NIP, ni pagos por adelantado fuera del proceso oficial."]].map(([t, d], i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /><h3 className="font-semibold text-slate-900">{t}</h3></div>
                  <p className="mt-2 text-sm text-slate-600">{d}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl bg-slate-900 p-6 text-white">
              <h3 className="font-semibold">¿Detectaste un intento de fraude?</h3>
              <p className="mt-1 text-sm text-slate-300">Repórtalo de inmediato a hola@circulodorado.mx o por WhatsApp oficial.</p>
            </div>
          </TabsContent>

          <TabsContent value="legal" className="mt-8">
            <div className="grid gap-4 sm:grid-cols-2">
              {["Bases de los sorteos", "Aviso de privacidad", "Términos y condiciones", "Políticas de premios", "Restricciones de participación", "Permiso de autoridad (demo)"].map((d) => (
                <div key={d} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-brand" data-testid={`legal-${d}`}>
                  <FileText className="h-5 w-5 text-brand" /><span className="font-medium text-slate-800">{d}</span>
                  <span className="ml-auto text-xs text-slate-400">PDF · demo</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="process" className="mt-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
                <h3 className="mb-6 font-semibold text-slate-900">Timeline público de transparencia</h3>
                <Timeline items={timeline} />
              </div>
              <div className="space-y-4">
                {[[Building2, "Identidad", "Círculo Dorado S.A. de C.V., fundada en 2019 en Guadalajara, Jalisco."],
                  [Users, "Equipo", "Un equipo dedicado a operar sorteos con transparencia y trazabilidad."],
                  [MapPin, "Oficinas", "Av. Vallarta 1234, Guadalajara, Jalisco. Lun–Vie 9:00–18:00."]].map(([Icon, t, d], i) => (
                  <div key={i} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-50 text-brand"><Icon className="h-5 w-5" /></div>
                    <div><h4 className="font-semibold text-slate-900">{t}</h4><p className="mt-1 text-sm text-slate-600">{d}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Section>
    </div>
  );
}
